import { useState, useEffect } from 'react'
import type { ModelsData, ModelGroups, ModelData } from '../types'
import CategoryGrid from './CategoryGrid'
import ModelGrid from './ModelGrid'
import type { Category } from './CategoryGrid'
import { useCategoryGridPreloader } from '../hooks/useImagePreloader'

interface NewModelSelectorProps {
  modelsData: ModelsData
  modelGroups: ModelGroups
  onModelChange: (model: ModelData | null, groupKey?: string, modelKey?: string) => void
  selectedGroup?: string
  selectedModel?: string
}

type ViewState = 'categories' | 'models' | 'configuration'

const NewModelSelector = ({ 
  modelsData, 
  modelGroups, 
  onModelChange, 
  selectedGroup: externalGroup, 
  selectedModel: externalModel
}: NewModelSelectorProps) => {
  const [viewState, setViewState] = useState<ViewState>('categories')
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [selectedGroup, setSelectedGroup] = useState('')
  const [selectedModel, setSelectedModel] = useState('')

  // Preload category grid images first
  const { isLoading: categoryImagesLoading } = useCategoryGridPreloader(modelsData)
  
  // Don't use model preloader here - models will be preloaded individually when displayed

  // Update internal state when external props change
  useEffect(() => {
    if (externalGroup !== undefined) {
      setSelectedGroup(externalGroup)
      
      // If we have a group, we should be in configuration view
      if (externalGroup && externalModel) {
        setViewState('configuration')
        // Find category for this group
        const category = findCategoryByGroup(externalGroup)
        if (category) {
          setSelectedCategory(category)
        }
      } else if (externalGroup) {
        setViewState('models')
        const category = findCategoryByGroup(externalGroup)
        if (category) {
          setSelectedCategory(category)
        }
      }
    }
  }, [externalGroup])

  useEffect(() => {
    if (externalModel !== undefined) {
      setSelectedModel(externalModel)
    }
  }, [externalModel])

  const findCategoryByGroup = (groupKey: string): Category | null => {
    // This mapping mirrors CategoryGrid's categories
    const categoryMap: { [key: string]: Category } = {
      'dragon-proste': {
        id: 'dragon-proste',
        title: 'Dragon Proste',
        description: 'Wkłady powietrzne Dragon z prostą szybą',
        image: '',
        groups: ['dragon-proste', 'dragon-proste-aku']
      },
      'dragon-proste-aku': {
        id: 'dragon-proste',
        title: 'Dragon Proste',
        description: 'Wkłady powietrzne Dragon z prostą szybą',
        image: '',
        groups: ['dragon-proste', 'dragon-proste-aku']
      },
      'dragon-narozne': {
        id: 'dragon-narozne',
        title: 'Dragon Narożne',
        description: 'Wkłady narożne Dragon do zabudowy w rogu',
        image: '',
        groups: ['dragon-narozne', 'dragon-narozne-aku']
      },
      'dragon-narozne-aku': {
        id: 'dragon-narozne',
        title: 'Dragon Narożne',
        description: 'Wkłady narożne Dragon do zabudowy w rogu',
        image: '',
        groups: ['dragon-narozne', 'dragon-narozne-aku']
      },
      'dragon-vis-a-vis': {
        id: 'dragon-vis-a-vis',
        title: 'Dragon Vis-a-vis',
        description: 'Wkłady dwustronne Dragon z szybą vis-a-vis',
        image: '',
        groups: ['dragon-vis-a-vis', 'dragon-vis-a-vis-aku']
      },
      'dragon-vis-a-vis-aku': {
        id: 'dragon-vis-a-vis',
        title: 'Dragon Vis-a-vis',
        description: 'Wkłady dwustronne Dragon z szybą vis-a-vis',
        image: '',
        groups: ['dragon-vis-a-vis', 'dragon-vis-a-vis-aku']
      },
      'dragon-portal': {
        id: 'dragon-portal',
        title: 'Dragon Portal',
        description: 'Wkłady Dragon z portalem do zabudowy',
        image: '',
        groups: ['dragon-portal']
      },
      'dragon-trojstronne': {
        id: 'dragon-trojstronne',
        title: 'Dragon Trójstronne',
        description: 'Trójstronne przeszklenie zapewnia wizję ognia w palenisku o bardzo dużym spektrum.',
        image: '',
        groups: ['dragon-trojstronne']
      },
      'dragon-polokragle': {
        id: 'dragon-polokragle',
        title: 'Dragon Półokrągłe',
        description: 'Wkłady Dragon z półokrągłą szybą',
        image: '',
        groups: ['dragon-polokragle']
      },
      'nemo-proste': {
        id: 'nemo-proste',
        title: 'Nemo Proste',
        description: 'Wkłady wodne Nemo z prostą szybą',
        image: '',
        groups: ['nemo-proste']
      },
      'nemo-narozne': {
        id: 'nemo-narozne',
        title: 'Nemo Narożne',
        description: 'Wkłady narożne Nemo do zabudowy w rogu',
        image: '',
        groups: ['nemo-narozne']
      },
      'nemo-vis-a-vis': {
        id: 'nemo-vis-a-vis',
        title: 'Nemo Vis-a-vis',
        description: 'Wkłady dwustronne Nemo z szybą vis-a-vis',
        image: '',
        groups: ['nemo-vis-a-vis']
      }
    }
    
    return categoryMap[groupKey] || null
  }

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category)
    setViewState('models')
    window.scrollTo(0, 0)
  }

  const handleModelSelect = (model: ModelData, groupKey: string, modelKey: string) => {
    setSelectedGroup(groupKey)
    setSelectedModel(modelKey)
    setViewState('configuration')
    onModelChange(model, groupKey, modelKey)
    window.scrollTo(0, 0)
  }

  const handleBackToCategories = () => {
    setViewState('categories')
    setSelectedCategory(null)
    window.scrollTo(0, 0)
  }

  const handleBackToModels = () => {
    if (selectedCategory) {
      setViewState('models')
      // Clear model selection
      setSelectedGroup('')
      setSelectedModel('')
      onModelChange(null, '', '')
    } else {
      setViewState('categories')
    }
    window.scrollTo(0, 0)
  }

  const currentModel = selectedGroup && selectedModel ? modelsData[selectedGroup]?.[selectedModel] : null

  return (
    <div className="new-model-selector">
      <div className="option-group">
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: 0 }}>
            {viewState === 'categories' && 'WYBÓR KATEGORII'}
            {viewState === 'models' && selectedCategory && selectedCategory.title}
            {viewState === 'configuration' && currentModel && `${currentModel.name} (${currentModel.power})`}
          </h3>
          {viewState === 'categories' && (
            <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666', textAlign: 'center' }}>
              Wybierz kategorię wkładu kominkowego
            </p>
          )}
          {viewState === 'models' && selectedCategory && (
            <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666', textAlign: 'center' }}>
              {selectedCategory.description}
            </p>
          )}
        </div>

        {viewState === 'categories' && (
          categoryImagesLoading ? (
            <div className="loading-spinner-container" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              gap: '15px'
            }}>
              <div className="spinner"></div>
              <p style={{ margin: 0, color: 'var(--fg)', opacity: 0.7 }}>
                Ładowanie kategorii...
              </p>
            </div>
          ) : (
            <CategoryGrid
              modelsData={modelsData}
              modelGroups={modelGroups}
              onCategorySelect={handleCategorySelect}
            />
          )
        )}

        {viewState === 'models' && selectedCategory && (
          categoryImagesLoading ? (
            <div className="loading-spinner-container" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              gap: '15px'
            }}>
              <div className="spinner"></div>
              <p style={{ margin: 0, color: 'var(--fg)', opacity: 0.7 }}>
                Ładowanie modeli...
              </p>
            </div>
          ) : (
            <ModelGrid
              modelsData={modelsData}
              category={selectedCategory}
              onModelSelect={handleModelSelect}
              onBackToCategories={handleBackToCategories}
            />
          )
        )}

        {viewState === 'configuration' && currentModel && (
          <div className="model-configuration-header">
            <button 
              className="back-button"
              onClick={handleBackToModels}
            >
              Zmień model
            </button>
            <div className="model-info">
              <div className="model-details">
                {currentModel.power} | {currentModel.type.includes('powietrzny') ? 'Powietrzny' : 'Wodny'} | {currentModel.glassType === 'prosta' ? 'Szyba prosta' : currentModel.glassType === 'vis-a-vis' ? 'Vis-a-vis' : 'Narożny'}
              </div>
              {currentModel.specialNote && (
                <div className="model-special-note">
                  {currentModel.specialNote}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default NewModelSelector